import {
  BuildingOfficeIcon,
  CameraIcon,
  StarIcon,
  TrashIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Button,
  FileButton,
  Group,
  Loader,
  Menu,
  Select,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../../../../auth/AuthProvider";
import { useEditMode } from "../../../../../context/EditModeContext";
import { ProfileCompletionPanel } from '../../../../../features/ProfileCompletion/components/ProfileCompletionPanel';
import { OfficeLocationDictionaryModel } from "../../../../../models/officeLocation.model";
import { UserRole } from "../../../../../models/user.model";
import { OfficeLocationService } from "../../../../../services/officeLocationService";
import { PersonService } from "../../../../../services/personService";
import { PersonTechnologyService } from "../../../../../services/personTechnologyService";
import { UserCV } from "../../../../../types/cv";
import { PersonTechnology } from "../../../../../types/technology";

interface ProfileCardProps {
  cv: UserCV;
  onPhotoUpdate?: (base64Image: string) => void;
  onPhotoRemove?: () => void;
  onUseAccountPhoto?: () => void;
  onOfficeLocationChange?: (officeLocationId: number | null) => void;
  lastEditedAt?: number;
}

// Helper function to get initials for avatar
const getInitials = (name: string) => {
  const nameParts = name.split(" ");
  if (nameParts.length >= 2) {
    return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(
      0
    )}`;
  }
  return nameParts[0].charAt(0);
};

// Helper function to convert file to base64
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

export function ProfileCard({
  cv,
  onPhotoUpdate,
  onPhotoRemove,
  onUseAccountPhoto,
  onOfficeLocationChange,
  lastEditedAt,
}: ProfileCardProps) {
  const [uploading, setUploading] = useState(false);
  const [menuOpened, setMenuOpened] = useState(false);
  const uploadButtonRef = useRef<HTMLButtonElement>(null);
  const [favoriteTechnologies, setFavoriteTechnologies] = useState<
    PersonTechnology[]
  >([]);
  const [favoritesLoading, setFavoritesLoading] = useState<boolean>(true);
  const [officeLocations, setOfficeLocations] = useState<
    OfficeLocationDictionaryModel[]
  >([]);
  const [officeLocationsLoading, setOfficeLocationsLoading] = useState(false);
  const [syncingOfficeLocation, setSyncingOfficeLocation] = useState(false);
  const [fullImageUrl, setFullImageUrl] = useState<string | undefined>(undefined);

  const { userRole, currentUser } = useAuth();
  const { isEditMode } = useEditMode();

  const personService = new PersonService();
  const officeLocationService = new OfficeLocationService();

  useEffect(() => {
    if (cv?.profilePhoto) {
      const profilePhoto = cv.profilePhoto;

      if (typeof profilePhoto === 'string' && (
        profilePhoto.startsWith('data:image') ||
        profilePhoto.match(/^[A-Za-z0-9+/=]+$/)
      )) {
        if (!profilePhoto.startsWith('data:image')) {
          setFullImageUrl(`data:image/jpeg;base64,${profilePhoto}`);
        } else {
          setFullImageUrl(profilePhoto);
        }
      }
      else if (typeof profilePhoto === 'string' && (
        profilePhoto.startsWith('http://') ||
        profilePhoto.startsWith('https://')
      )) {
        setFullImageUrl(profilePhoto);
      }
      else if (typeof profilePhoto === 'string') {
        setFullImageUrl(`${import.meta.env.REACT_APP_API_URL || ""}${profilePhoto}`);
      }
      else {
        setFullImageUrl(undefined);
      }
    } else {
      setFullImageUrl(undefined);
    }
  }, [cv?.profilePhoto]);

  useEffect(() => {
    if (!cv?.personId) return;

    const fetchFavorites = async () => {
      try {
        setFavoritesLoading(true);
        const personTechnologyService = new PersonTechnologyService();
        const modelFavorites =
          await personTechnologyService.getPersonFavoriteTechnologies(
            parseInt(cv.personId)
          );

        const typeFavorites: PersonTechnology[] = modelFavorites.map((fav) => ({
          id: fav.id,
          technology: {
            id: fav.technology.id,
            name: fav.technology.name,
            categories: (fav.technology as any).categories || [],
          },
          personId: fav.personId,
          favourite: fav.favourite,
          skillLevel: null,
          yearsOfExperience: null,
          projects: [],
          workExperiences: [],
        }));

        setFavoriteTechnologies(typeFavorites);
      } catch (error) {
        console.error("Failed to fetch favorite technologies:", error);
      } finally {
        setFavoritesLoading(false);
      }
    };

    fetchFavorites();
  }, [cv?.personId]);

  useEffect(() => {
    if (!isEditMode) return;

    const fetchOfficeLocations = async () => {
      try {
        setOfficeLocationsLoading(true);
        const locations =
          await officeLocationService.getOfficeLocationsDictionary();
        setOfficeLocations(locations);
        console.log("Office locations loaded:", locations);
        console.log("Current CV office location ID:", cv?.officeLocationId);
      } catch (error) {
        console.error("Failed to fetch office locations:", error);
      } finally {
        setOfficeLocationsLoading(false);
      }
    };

    fetchOfficeLocations();
  }, [isEditMode, cv?.officeLocationId]);

  const isEditingOwnCV = currentUser?.personId === parseInt(cv?.personId || "0");

  const canEditOfficeLocation =
    isEditMode &&
    (userRole === UserRole.Admin || userRole === UserRole.Manager);

  console.log("ProfileCard debug:", {
    userRole,
    isEditMode,
    isEditingOwnCV,
    canEditOfficeLocation,
    currentUserPersonId: currentUser?.personId,
    cvPersonId: cv?.personId,
  });

  const showSyncButton = isEditMode && isEditingOwnCV;

  const handlePhotoUpload = async (file: File | null) => {
    if (!file || !onPhotoUpdate) return;

    try {
      setUploading(true);
      setMenuOpened(false); // Close menu when upload starts
      const base64Image = await fileToBase64(file);
      onPhotoUpdate(base64Image);
    } catch (error) {
      console.error("Error uploading photo:", error);
    } finally {
      setUploading(false);
    }
  };

  const handleSyncOfficeLocation = async () => {
    if (!cv?.personId) return;

    try {
      setSyncingOfficeLocation(true);
      const updatedPerson = await personService.syncOfficeLocationFromGraph(
        parseInt(cv.personId)
      );

      if (
        onOfficeLocationChange &&
        updatedPerson.officeLocationId !== cv.officeLocationId
      ) {
        onOfficeLocationChange(updatedPerson.officeLocationId || null);
        notifications.show({
          title: "Success",
          message: "Office location synchronized from your company profile",
          color: "green",
        });
      } else {
        notifications.show({
          title: "No Changes",
          message:
            "No office location found in your company profile or it matches your current location",
          color: "blue",
        });
      }
    } catch (error) {
      console.error("Error syncing office location:", error);
      notifications.show({
        title: "Error",
        message: "Failed to sync office location from your company profile",
        color: "red",
      });
    } finally {
      setSyncingOfficeLocation(false);
    }
  };

  const handleOfficeLocationChange = (value: string | null) => {
    const locationId = value ? parseInt(value) : null;
    if (onOfficeLocationChange) {
      onOfficeLocationChange(locationId);
    }
  };

  const handleUploadMenuItemClick = () => {
    setMenuOpened(false);
    if (uploadButtonRef.current) {
      uploadButtonRef.current.click();
    }
  };

  return (
    <Box>
      <Group>
        <Box style={{ position: "relative" }}>
          <Avatar
            size={120}
            radius={120}
            color="brand"
            style={{ fontSize: "3rem" }}
            src={fullImageUrl}
          >
            {!fullImageUrl && getInitials(cv.name)}
          </Avatar>

          {onPhotoUpdate && (
            <>
              <FileButton
                onChange={handlePhotoUpload}
                accept="image/png,image/jpeg,image/jpg"
              >
                {(props) => (
                  <button
                    ref={uploadButtonRef}
                    {...props}
                    style={{ display: "none" }}
                  />
                )}
              </FileButton>

              <Menu
                position="bottom-end"
                withArrow
                opened={menuOpened}
                onChange={setMenuOpened}
              >
                <Menu.Target>
                  <ActionIcon
                    color="brand"
                    variant="filled"
                    radius="xl"
                    size="md"
                    loading={uploading}
                    style={{
                      position: "absolute",
                      bottom: 0,
                      right: 0,
                    }}
                  >
                    <CameraIcon width={16} />
                  </ActionIcon>
                </Menu.Target>

                <Menu.Dropdown>
                  <Menu.Label>Manage photo</Menu.Label>

                  <Menu.Item
                    leftSection={<CameraIcon width={16} />}
                    onClick={handleUploadMenuItemClick}
                  >
                    Upload photo
                  </Menu.Item>

                  {onUseAccountPhoto && (
                    <Menu.Item
                      leftSection={<UserCircleIcon width={16} />}
                      onClick={() => {
                        setMenuOpened(false);
                        onUseAccountPhoto();
                      }}
                    >
                      Use profile photo
                    </Menu.Item>
                  )}

                  {onPhotoRemove && cv?.profilePhoto && (
                    <Menu.Item
                      color="red"
                      leftSection={<TrashIcon width={16} />}
                      onClick={() => {
                        setMenuOpened(false);
                        onPhotoRemove();
                      }}
                    >
                      Remove photo
                    </Menu.Item>
                  )}
                </Menu.Dropdown>
              </Menu>
            </>
          )}
        </Box>

        <Box>
          <Group align="center" mb="xs">
            <Text size="xl" fw={700}>
              {cv?.name || "Loading..."}
              {cv.personId ? ` (ID: ${cv.personId})` : ""}
            </Text>
          </Group>

          <Text size="lg" c="dimmed" mb="md">
            {cv.title}
          </Text>

          <Group align="flex-end">
            <Box>
              <Text size="sm" c="dimmed">
                EMAIL
              </Text>
              <Text>{cv.email}</Text>
            </Box>
            <Box>
              <Group align="flex-end" gap="xs">
                <Box style={{ minWidth: "200px" }}>
                  <Text size="sm" c="dimmed">
                    OFFICE LOCATION
                  </Text>
                  {isEditMode ? (
                    <Group gap="xs" align="flex-end">
                      <Select
                        placeholder="Select office location"
                        data={officeLocations.map((loc) => ({
                          value: loc.id.toString(),
                          label: loc.name,
                        }))}
                        value={
                          cv.officeLocationId !== null &&
                            cv.officeLocationId !== undefined
                            ? cv.officeLocationId.toString()
                            : null
                        }
                        onChange={handleOfficeLocationChange}
                        disabled={
                          !canEditOfficeLocation || officeLocationsLoading
                        }
                        size="sm"
                        style={{ flex: 1, minWidth: "180px" }}
                        searchable
                        clearable
                        error={false}
                        withErrorStyles={false}
                      />
                      {showSyncButton && (
                        <Button
                          variant="light"
                          size="sm"
                          leftSection={<BuildingOfficeIcon width={16} />}
                          onClick={handleSyncOfficeLocation}
                          loading={syncingOfficeLocation}
                          style={{ whiteSpace: "nowrap" }}
                        >
                          Fill from Company Profile
                        </Button>
                      )}
                    </Group>
                  ) : (
                    <Text>
                      {cv.officeLocation
                        ? `📍 ${cv.officeLocation}`
                        : "Not specified"}
                    </Text>
                  )}
                </Box>
              </Group>
            </Box>
          </Group>

        </Box>
      </Group>

      {/* Profile Completeness Section */}
      {cv?.personId && (
        <Box
          mt="sm"
          pt="sm"
          style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}
        >
          <ProfileCompletionPanel personId={parseInt(cv.personId)} lastEditedAt={lastEditedAt} />
        </Box>
      )}

      {/* Favorite Technologies Section */}
      <Box
        mt="sm"
        pt="sm"
        style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}
      >
        <Group mb="xs">
          <StarIcon width={18} />
          <Text fw={600} size="sm">
            Favorite Skills & Technologies
          </Text>
        </Group>

        {favoritesLoading ? (
          <Box py="md" style={{ display: "flex", justifyContent: "center" }}>
            <Loader size="sm" />
          </Box>
        ) : favoriteTechnologies.length === 0 ? (
          <Text size="sm" c="dimmed" fs="italic">
            No favorite skills & technologies found.
          </Text>
        ) : (
          <Group gap="xs">
            {favoriteTechnologies.map((tech) => (
              <Badge key={tech.id} size="md" color="blue" variant="filled">
                {tech.technology.name}
              </Badge>
            ))}
          </Group>
        )}
      </Box>
    </Box>
  );
}

